import { createFileRoute } from "@tanstack/react-router";
import { InstantQuoteBuilder } from "@/components/instant-quote-builder";
import { HomeReviews } from "@/components/home-reviews";
import { GoogleReviewPopup } from "@/components/google-review-popup";
import { SeasonBanner } from "@/components/season-banner";

import {
  houseWashAfterOpt,
  houseWashBeforeOpt,
  roofWashAfterOpt,
  roofWashBeforeOpt,
  houseWashingHeroOpt,
  concreteCleaningHeroOpt,
  paverCliftonParkHeroOpt,
  commercialWashingHeroOpt,
} from "@/assets/opt";
import gutterCleaningHero from "@/assets/guide-fall-gutter-checklist.webp.asset.json";
import windowCleaningHero from "@/assets/service-heroes/service-window-cleaning.webp.asset.json";
import { GalleryVideoSlider } from "@/components/gallery-video-slider";
import { Check, Phone, Star, X } from "lucide-react";
import appCss from "@/styles.css?url";

const SITE = "https://capitalpropressurewashing.com";

export const Route = createFileRoute("/instant-quote")({
  head: () => ({
    meta: [
      { title: "Instant Soft Washing & Pressure Washing Quote | Capital Pro" },
      {
        name: "description",
        content:
          "Get a real soft washing and pressure washing price in about 30 seconds. Pick your services, tell us your home size, and see your instant quote. Albany & the Capital Region.",
      },
      { property: "og:title", content: "Instant Soft Washing & Pressure Washing Quote | Capital Pro" },
      {
        property: "og:description",
        content:
          "Three quick questions, one real price. Instant soft washing and pressure washing quotes for Albany & the Capital Region.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [
      { rel: "canonical", href: `${SITE}/instant-quote` },
      // This page's layout lives entirely in styles.css (the .iq-* rules) and
      // is NOT part of the inlined critical CSS, so the site-wide deferred
      // stylesheet caused a flash of unstyled text here. Load it render-blocking
      // on this route only.
      { rel: "stylesheet", href: appCss },
    ],
  }),
  component: InstantQuotePage,
});

const TRUST = [
  "Most estimates quoted the same day",
  "5.0 rating from Capital Region homeowners",
  "No site visit needed for most homes",
];

function InstantQuotePage() {
  return (
    <main className="iq-page">
      {/* This landing page has no site header, so the seasonal availability bar
          the rest of the site shows in the sticky header is rendered here. */}
      <SeasonBanner className="border-b border-white/15" />
      <section className="iq-hero">
        <div className="iq-hero-inner">
          {/* LEFT — pitch */}
          <div className="iq-hero-copy">
            <p className="iq-eyebrow">
              <span className="iq-eyebrow-dot" aria-hidden="true" /> Capital Region Homeowners
            </p>
            <h1 className="iq-h1">
              Make your home look brand new with soft washing and <RotatingAccent /> in
              the next three days — without lifting a finger.
            </h1>
            <p className="iq-lede">
              Skip the phone tag, onsite estimate and pushy sales pitch. Answer three quick questions
              to see your instant pressure washing price in about 30 seconds — bundle discounts
              included.
            </p>
            <p className="iq-rating">
              <span className="iq-stars" aria-hidden="true">
                <Star className="h-4 w-4 fill-current" />
                <Star className="h-4 w-4 fill-current" />
                <Star className="h-4 w-4 fill-current" />
                <Star className="h-4 w-4 fill-current" />
                <Star className="h-4 w-4 fill-current" />
              </span>
              <strong>5.0</strong> <span>· Google reviews</span>
            </p>

            <div className="iq-hero-clips">
              <p className="iq-photo-tag">
                <span className="iq-eyebrow-dot" aria-hidden="true" /> Real Capital Pro work
              </p>
              <GalleryVideoSlider />
            </div>

            <p className="iq-hero-call">
              Rather talk it through?{" "}
              <a href="tel:+15189001913">
                <Phone className="h-4 w-4" aria-hidden="true" /> (518) 900-1913
              </a>
            </p>
          </div>

          {/* RIGHT — calculator */}
          <div id="instant-quote" className="iq-hero-form">
            <div className="iq-form-head">
              <h2 className="iq-form-title">Build Your Instant Quote</h2>
              <p className="iq-form-sub">Three quick questions and your price appears on screen</p>
            </div>
            <InstantQuoteBuilder />
          </div>
        </div>

        <div className="iq-trust-strip">
          <ul>
            {TRUST.map((t) => (
              <li key={t}>
                <span className="iq-trust-check" aria-hidden="true">
                  <Check className="h-3.5 w-3.5" />
                </span>
                {t}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* SERVICES */}
      <section className="iqs-sec iqs-sec-light">
        <div className="iqs-wrap">
          <p className="iqs-kicker">One instant quote covers it all</p>
          <h2 className="iqs-h2">
            Everything outside your Capital Region home can look cared for again.
          </h2>
          <p className="iqs-lede">
            Choose one service or bundle several together. We use the right cleaning method for each
            surface, then give you one clear price range before anybody calls.
          </p>
          <div className="iqs-svc-grid">
            {SERVICES.map((s) => (
              <article key={s.title} className="iqs-svc">
                <img src={s.img} alt={s.alt} loading="lazy" decoding="async" width={600} height={400} />
                <div className="iqs-svc-body">
                  <h3>
                    <span className="iqs-tick" aria-hidden="true">
                      <Check className="h-3.5 w-3.5" />
                    </span>
                    {s.title}
                  </h3>
                  <p>{s.copy}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="iqs-sec">
        <div className="iqs-wrap">
          <p className="iqs-kicker">How it works</p>
          <h2 className="iqs-h2">From dirty to scheduled in three simple steps.</h2>
          <ol className="iqs-steps">
            {STEPS.map((s, i) => (
              <li key={s.title}>
                <div className="iqs-step-top">
                  <span className="iqs-step-num">0{i + 1}</span>
                  <span className="iqs-step-note">{s.note}</span>
                </div>
                <h3>{s.title}</h3>
                <p>{s.copy}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* OLD WAY VS OUR WAY */}
      <section className="iqs-sec iqs-sec-light">
        <div className="iqs-wrap">
          <p className="iqs-kicker">A faster way to get started</p>
          <h2 className="iqs-h2">
            Getting an exterior cleaning quote should not become another chore.
          </h2>
          <p className="iqs-lede">
            Capital Pro gives Capital Region homeowners a useful price first, then verifies the
            property remotely in most cases.
          </p>
          <div className="iqs-compare">
            <div className="iqs-col iqs-col-bad">
              <p className="iqs-col-kicker">The old way</p>
              <h3>Days of calls, visits and waiting</h3>
              <ul>
                {OLD_WAY.map((t) => (
                  <li key={t}>
                    <X className="h-4 w-4 shrink-0" aria-hidden="true" />
                    {t}
                  </li>
                ))}
              </ul>
              <p className="iqs-col-foot">Too much effort before you even see a price</p>
            </div>
            <div className="iqs-col iqs-col-good">
              <p className="iqs-col-kicker">The Capital Pro way</p>
              <h3>See your number before the phone rings</h3>
              <ul>
                {NEW_WAY.map((t) => (
                  <li key={t}>
                    <Check className="h-4 w-4 shrink-0" aria-hidden="true" />
                    {t}
                  </li>
                ))}
              </ul>
              <p className="iqs-col-foot">Your price takes about 30 seconds</p>
            </div>
          </div>
        </div>
      </section>

      {/* LOCAL BAND */}
      <section className="iqs-band">
        <div className="iqs-wrap">
          <p className="iqs-kicker iqs-kicker-light">Built for Capital Region homes</p>
          <h2 className="iqs-h2 iqs-h2-light">
            Upstate moisture, shade, pollen and algae do not wait for a callback.
          </h2>
          <p className="iqs-lede iqs-lede-light">
            We match pressure, detergents and equipment to the surface being cleaned — soft washing
            for siding and roofs, purified water for glass, and professional surface cleaning for
            concrete.
          </p>
          <div className="iqs-band-actions">
            <a href="#instant-quote" className="qm-btn-primary">
              See My Instant Price →
            </a>
            <a href="tel:+15189001913" className="qm-btn-call">
              <Phone className="h-4 w-4" aria-hidden="true" /> (518) 900-1913
            </a>
          </div>
        </div>
      </section>

      {/* RESULTS */}
      <section className="iqs-sec">
        <div className="iqs-wrap">
          <p className="iqs-kicker">Real Capital Pro results</p>
          <h2 className="iqs-h2">See what professional exterior cleaning can change.</h2>
          <div className="iqs-results">
            {RESULTS.map((r) => (
              <figure key={r.label} className="iqs-result">
                <div className="iqs-result-pair">
                  <img src={r.before} alt={`${r.label} before`} loading="lazy" decoding="async" />
                  <img src={r.after} alt={`${r.label} after`} loading="lazy" decoding="async" />
                </div>
                <figcaption>{r.label}</figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* REVIEWS */}
      <HomeReviews />

      {/* FAQ */}
      <section className="iqs-sec iqs-sec-light">
        <div className="iqs-wrap iqs-wrap-narrow">
          <p className="iqs-kicker">Common questions</p>
          <h2 className="iqs-h2">What to know before you request your quote.</h2>
          <div className="iqs-faq">
            {FAQS.map((f) => (
              <details key={f.q}>
                <summary>{f.q}</summary>
                <p>{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="iqs-final">
        <div className="iqs-wrap">
          <p className="iqs-kicker iqs-kicker-light">Your Capital Region home can look new again with soft washing & power washing</p>
          <h2 className="iqs-h2 iqs-h2-light">
            See your price before you commit to anything.
          </h2>
          <p className="iqs-lede iqs-lede-light">
            Choose your services, enter your home size and get your instant price in about 30 seconds.
          </p>
          <div className="iqs-band-actions">
            <a href="#instant-quote" className="qm-btn-primary">
              Build My Instant Quote →
            </a>
            <a href="tel:+15189001913" className="qm-btn-call">
              <Phone className="h-4 w-4" aria-hidden="true" /> Call (518) 900-1913
            </a>
          </div>
          <p className="iqs-final-note">No onsite estimate required for most homes.</p>
        </div>
      </section>

      <GoogleReviewPopup />
    </main>
  );
}


const SERVICES = [
  {
    title: "House Washing",
    img: houseWashingHeroOpt.url,
    alt: "Capital Pro technician soft washing residential siding",
    copy: "A low-pressure exterior wash for algae, dirt, pollen and cobwebs on siding, trim and eaves.",
  },
  {
    title: "Roof Washing",
    img: roofWashAfterOpt.url,
    alt: "Clean shingle roof after a Capital Pro soft wash",
    copy: "Soft washing removes black streaks and organic buildup without treating shingles like concrete.",
  },
  {
    title: "Gutter Cleaning",
    img: gutterCleaningHero.url,
    alt: "Capital Pro technician cleaning gutters and checking downspouts on a Capital Region home",
    copy: "Interior debris removal and a downspout check help rainwater move away from your home.",
  },
  {
    title: "Window Cleaning",
    img: windowCleaningHero.url,
    alt: "Streak-free window cleaning results on a Capital Region home",
    copy: "Exterior glass is cleaned with a water-fed pole and purified rinse. Interior glass available on request.",
  },
  {
    title: "Concrete Cleaning",
    img: concreteCleaningHeroOpt.url,
    alt: "Surface cleaning a concrete driveway",
    copy: "Driveways, sidewalks and patios are cleaned with equipment matched to the surface.",
  },
  {
    title: "Paver Cleaning & Sealing",
    img: paverCliftonParkHeroOpt.url,
    alt: "Restored and sealed paver patio",
    copy: "Deep cleaning, re-sanding and sealing that locks in color and slows weed and ant return.",
  },
];

const STEPS = [
  {
    note: "About 30 seconds",
    title: "Build your price online",
    copy: "Pick the services you need and your approximate home size. Your price appears immediately.",
  },
  {
    note: "Usually no site visit",
    title: "We verify the property",
    copy: "We review satellite imagery and property details to confirm size, access and job specifics.",
  },
  {
    note: "Clear communication",
    title: "Approve and schedule",
    copy: "Once the final price is confirmed, you get scheduling details and reminders by text and email.",
  },
];

const OLD_WAY = [
  "Submit a form and wait for a callback",
  "Find time for an onsite estimate",
  "Wait again while somebody works up a number",
  "Start over if the quote is outside your budget",
];

const NEW_WAY = [
  "Answer three quick questions online",
  "See a real price range on your screen",
  "Let us verify most homes remotely",
  "Schedule only when the final number works for you",
];

const RESULTS = [
  { label: "House Washing", before: houseWashBeforeOpt.url, after: houseWashAfterOpt.url },
  { label: "Roof Washing", before: roofWashBeforeOpt.url, after: roofWashAfterOpt.url },
];

const FAQS = [
  {
    q: "Do you need to visit my property to quote it?",
    a: "Not usually. Most homes can be priced from the details you give us plus satellite imagery. We schedule a visit only when the job can't be assessed accurately that way.",
  },
  {
    q: "Will you use high pressure on my roof or siding?",
    a: "No. We soft wash roofs and siding at low pressure, and reserve pressure-cleaning equipment for hard surfaces like concrete.",
  },
  {
    q: "How quickly will I hear back?",
    a: "Most quote requests are reviewed the same day. Timing can vary when a property has unusual access, materials or specialty cleaning needs.",
  },
  {
    q: "Where does Capital Pro work?",
    a: "Albany, Schenectady, Troy, Clifton Park, Saratoga Springs, Rensselaer and the surrounding Capital Region communities.",
  },
  {
    q: "Is the online number the final price?",
    a: "It's a price range based on the services and home size you select. We verify the property and confirm the final price with you before any work is scheduled.",
  },
];

function RotatingAccent() {
  const words = [
    "house washing",
    "gutter cleaning",
    "concrete cleaning",
    "roof washing",
    "window cleaning",
    "paver cleaning & sealing",
  ];
  return (
    <span className="iq-h1-accent" aria-live="polite" aria-atomic="true">
      <span className="iq-rotator">
        {words.map((w) => (
          <span key={w} className="iq-rotator-word">
            {w}
          </span>
        ))}
      </span>
    </span>
  );
}
